import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Plus, Search, Filter, FolderOpen, Clock, CheckCircle, AlertCircle, TrendingUp } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { getProjectsByOrganization } from '../../services/projectService'
import { getUserOrganizations } from '../../services/organizationService'
import { getOrganizationUsage } from '../../services/usageService'
import { useConfirm } from '../../hooks/useConfirm'
import { useToast } from '../../hooks/useToast'
import ConfirmDialog from '../../components/ConfirmDialog'
import Toast from '../../components/Toast'

const statusColors = {
  draft: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
  in_review: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  changes_requested: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  archived: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
}

const statusIcons = {
  draft: FolderOpen,
  in_review: Clock,
  approved: CheckCircle,
  changes_requested: AlertCircle,
  archived: FolderOpen
}

export default function Projects() {
  const { user } = useAuthStore()
  const [searchParams] = useSearchParams()
  const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm()
  const { toastState, success, error: showError, closeToast } = useToast()
  const [projects, setProjects] = useState([])
  const [organizations, setOrganizations] = useState([])
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '')
  const [statusFilter, setStatusFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [usage, setUsage] = useState(null)
  const [currentUserRole, setCurrentUserRole] = useState(null)
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    client_name: '',
    client_email: ''
  })

  useEffect(() => {
    loadData()
  }, [user])

  useEffect(() => {
    // Reload data when page gains focus (e.g., after accepting invitation)
    const handleFocus = () => {
      loadData()
    }
    
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [user])

  useEffect(() => {
    // Update search query from URL parameter
    const urlSearch = searchParams.get('search')
    if (urlSearch) {
      setSearchQuery(urlSearch)
    }
  }, [searchParams])

  useEffect(() => {
    // Load projects when organization is selected or changed
    if (selectedOrg) {
      loadProjectsForOrg(selectedOrg)
    } else {
      setLoading(false)
    }
  }, [selectedOrg])

  const loadData = async () => {
    if (!user) return
    
    try {
      const { data: orgs } = await getUserOrganizations(user.id)
      setOrganizations(orgs || [])
      
      if (orgs && orgs.length > 0) {
        setSelectedOrg(orgs[0].id)
        // Set current user role from first organization
        setCurrentUserRole(orgs[0].memberRole)
      } else {
        setLoading(false)
      }
    } catch (error) {
      console.error('Error loading organizations:', error)
      setLoading(false)
    }
  }

  const loadProjectsForOrg = async (orgId) => {
    if (!orgId) return
    
    try {
      setLoading(true)
      const { data } = await getProjectsByOrganization(orgId)
      setProjects(data || [])
      console.log('Loaded projects:', data)
      
      // Load usage data for limit display
      const { data: usageData } = await getOrganizationUsage(orgId)
      setUsage(usageData)
      
      // Update current user role for selected organization
      const currentOrg = organizations.find(org => org.id === orgId)
      if (currentOrg) {
        setCurrentUserRole(currentOrg.memberRole)
      }
    } catch (error) {
      console.error('Error loading projects:', error)
      setProjects([])
    } finally {
      setLoading(false)
    }
  }

  const loadProjects = () => {
    if (selectedOrg) {
      loadProjectsForOrg(selectedOrg)
    }
  }

  const handleCreateProject = async (e) => {
    e.preventDefault()
    
    // Check permissions
    if (currentUserRole === 'viewer') {
      showError('You do not have permission to create projects. Contact your organization admin.')
      return
    }
    
    try {
      const { createProject } = await import('../../services/projectService')
      const data = await createProject({
        ...newProject,
        organization_id: selectedOrg,
        created_by: user.id,
        status: 'draft'
      })
      
      setShowCreateModal(false)
      setNewProject({ title: '', description: '', client_name: '', client_email: '' })
      await loadProjects()
      success('Project created successfully!')
    } catch (error) {
      console.error('Error creating project:', error)
      
      // Check if it's a limit error
      if (error.code === 'LIMIT_REACHED' && error.limitType === 'projects') {
        const upgrade = await confirm({
          title: 'Project Limit Reached',
          message: `${error.message}\n\nWould you like to upgrade your plan now?`,
          type: 'warning',
          confirmText: 'Upgrade Now',
          cancelText: 'Maybe Later'
        })
        if (upgrade) {
          window.location.hash = '/subscriptions'
        }
      } else {
        showError('Failed to create project: ' + error.message)
      }
    }
  }

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter
    
    return matchesSearch && matchesStatus
  })

  // Permission checks
  const canCreate = currentUserRole && ['owner', 'admin', 'member'].includes(currentUserRole)
  const isViewer = currentUserRole === 'viewer'

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Projects</h1>
            <p className="text-sm sm:text-base text-muted-light dark:text-muted-dark mt-1 sm:mt-2">
              Manage all your creative projects
            </p>
            {usage && (
              <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-2">
                <div className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium ${
                  usage.projects.percentage >= 90 
                    ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-200' 
                    : usage.projects.percentage >= 70
                    ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-200'
                    : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
                }`}>
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4" />
                  {usage.projects.unlimited ? (
                    <span>Unlimited Projects</span>
                  ) : (
                    <span>{usage.projects.total} / {usage.projects.limit} projects used</span>
                  )}
                </div>
                {usage.projects.percentage >= 80 && !usage.projects.unlimited && (
                  <Link 
                    to="/subscriptions"
                    className="text-xs sm:text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Upgrade for more →
                  </Link>
                )}
              </div>
            )}
          </div>
          <div className="relative group w-full sm:w-auto">
            <button
              onClick={() => {
                if (!canCreate) {
                  showError('Only members, admins and owners can create projects')
                  return
                }
                setShowCreateModal(true)
              }}
              disabled={!canCreate}
              className={`flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base w-full transition-all ${
                canCreate 
                  ? 'bg-black dark:bg-white text-white dark:text-black hover:opacity-90 cursor-pointer' 
                  : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
              title={!canCreate ? 'You need member, admin or owner role to create projects' : ''}
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              New Project
            </button>
            {isViewer && (
              <div className="absolute -bottom-8 left-0 right-0 text-xs text-center text-red-600 dark:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                Viewers cannot create projects
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {/* Organization Selector */}
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2">Organization</label>
            <select
              value={selectedOrg || ''}
              onChange={(e) => setSelectedOrg(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
            >
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for a project..."
                className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
            >
              <option value="all">All</option>
              <option value="draft">Draft</option>
              <option value="in_review">In Review</option>
              <option value="approved">Approved</option>
              <option value="changes_requested">Changes Requested</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12 sm:py-20">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-accent"></div>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-8 sm:p-12 text-center">
          <FolderOpen className="w-12 h-12 sm:w-16 sm:h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg sm:text-xl font-semibold mb-2">No Projects</h3>
          <p className="text-sm sm:text-base text-muted-light dark:text-muted-dark mb-6">
            Start by creating your first project
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-5 sm:px-6 py-2.5 sm:py-3 bg-black dark:bg-white text-white dark:text-black rounded-xl font-semibold hover:opacity-90 transition-opacity text-sm sm:text-base"
          >
            Create Project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
          {filteredProjects.map(project => {
            const StatusIcon = statusIcons[project.status] || FolderOpen
            
            return (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="bg-white dark:bg-surface-dark rounded-2xl shadow-soft p-5 sm:p-6 hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base sm:text-lg mb-1 truncate">{project.title}</h3>
                    <p className="text-xs sm:text-sm text-muted-light dark:text-muted-dark truncate">
                      {project.client_name}
                    </p>
                  </div>
                  <StatusIcon className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 ml-2 shrink-0" />
                </div>
                
                {project.description && (
                  <p className="text-xs sm:text-sm text-muted-light dark:text-muted-dark mb-3 sm:mb-4 line-clamp-2">
                    {project.description}
                  </p>
                )}
                
                <div className="flex items-center justify-between gap-2">
                  <span className={`px-2.5 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs font-medium ${statusColors[project.status]}`}>
                    {project.status === 'draft' && 'Draft'}
                    {project.status === 'in_review' && 'In Review'}
                    {project.status === 'approved' && 'Approved'}
                    {project.status === 'changes_requested' && 'Changes Requested'}
                    {project.status === 'archived' && 'Archived'}
                  </span>
                  <span className="text-[10px] sm:text-xs text-muted-light dark:text-muted-dark whitespace-nowrap">
                    {project.versions?.[0]?.count || 0} versions
                  </span>
                </div>
              </Link>
            )
          })}
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-xl max-w-lg w-full p-5 sm:p-6 lg:p-8 max-h-[90vh] overflow-y-auto">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 sm:mb-6">New Project</h2>
            
            <form onSubmit={handleCreateProject} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">Project Title *</label>
                <input
                  type="text"
                  required
                  value={newProject.title}
                  onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                  placeholder="Example: XYZ Company Logo Design"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">Description</label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                  placeholder="Project description..."
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">Client Name *</label>
                <input
                  type="text"
                  required
                  value={newProject.client_name}
                  onChange={(e) => setNewProject({ ...newProject, client_name: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                  placeholder="Client name"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium mb-2">Client Email</label>
                <input
                  type="email"
                  value={newProject.client_email}
                  onChange={(e) => setNewProject({ ...newProject, client_email: e.target.value })}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 outline-none text-sm sm:text-base"
                  placeholder="client@example.com"
                />
              </div>
              
              {/* Warning if approaching limit */}
              {usage && !usage.projects.unlimited && usage.projects.percentage >= 80 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-3 sm:p-4">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 dark:text-yellow-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs sm:text-sm font-medium text-yellow-900 dark:text-yellow-200">
                        You're using {usage.projects.total} of {usage.projects.limit} projects
                      </p>
                      <p className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                        {usage.projects.total >= usage.projects.limit ? (
                          <>You've reached your limit. <Link to="/subscriptions" className="font-medium underline">Upgrade now</Link></>
                        ) : (
                          <>Consider <Link to="/subscriptions" className="font-medium underline">upgrading</Link> for unlimited projects</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-3 pt-2 sm:pt-4">
                <button
                  type="submit"
                  className="flex-1 py-2.5 sm:py-3 bg-black dark:bg-white text-white dark:text-black rounded-xl font-semibold hover:opacity-90 transition-opacity text-sm sm:text-base"
                >
                  Create Project
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 py-2.5 sm:py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm sm:text-base"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={confirmState.title}
        message={confirmState.message}
        type={confirmState.type}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
      />

      {/* Toast Notification */}
      <Toast
        isOpen={toastState.isOpen}
        onClose={closeToast}
        message={toastState.message}
        type={toastState.type}
        duration={toastState.duration}
      />
    </div>
  )
}
